import React, { useEffect, useState, Fragment } from "react";
import { Row, Col, Card, Button, Modal, Form, Badge, Spinner } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";

const DeductionTypeMaster = () => {
 const [list, setList] = useState([]);
 const [filtered, setFiltered] = useState([]);
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);

 const [showModal, setShowModal] = useState(false);
 const [editId, setEditId] = useState(null);
 const [search, setSearch] = useState("");

 const [form, setForm] = useState({
 name: "",
 description: "",
 status: "Active",
 });

 /* =========================
 FETCH DEDUCTION TYPES
 ========================= */
 const fetchTypes = async () => {
 setLoading(true);
 try {
 const res = await api.get("/deduction-types?includeInactive=true");
 if (res.data.success) {
 setList(res.data.data);
 setFiltered(res.data.data);
 }
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchTypes();
 }, []);

 /* =========================
 SEARCH FILTER
 ========================= */
 useEffect(() => {
 if (!search.trim()) {
 setFiltered(list);
 } else {
 const s = search.toLowerCase();
 setFiltered(list.filter((i) => i.name.toLowerCase().includes(s)));
 }
 }, [search, list]);

 /* =========================
 MODAL HANDLERS
 ========================= */
 const openAdd = () => {
 setEditId(null);
 setForm({ name: "", description: "", status: "Active" });
 setShowModal(true);
 };

 const openEdit = (item) => {
 setEditId(item._id);
 setForm({
 name: item.name,
 description: item.description || "",
 status: item.status,
 });
 setShowModal(true);
 };

 const closeModal = () => {
 if (!saving) setShowModal(false);
 };

 const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

 /* =========================
 SAVE (CREATE / UPDATE)
 ========================= */
 const handleSave = async () => {
 if (!form.name.trim()) {
 return swal("Validation", "Deduction type name is required", "warning");
 }

 try {
 setSaving(true);
 if (editId) {
 await api.patch(`/deduction-types/${editId}`, form);
 } else {
 await api.post("/deduction-types", form);
 }

 swal("Success", "Deduction type saved successfully", "success");
 closeModal();
 fetchTypes();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Operation failed",
 "error"
 );
 } finally {
 setSaving(false);
 }
 };

 /* =========================
 DELETE / DEACTIVATE
 ========================= */
 const handleDelete = (item) => {
 swal({
 title: `Delete "${item.name}"?`,
 text: "This action will permanently remove the deduction type if it is not in use.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 }).then(async (ok) => {
 if (!ok) return;
 try {
 await api.delete(`/deduction-types/${item._id}`);
 swal("Deleted", "Deduction type removed successfully", "success");
 fetchTypes();
 } catch (err) {
 swal(
 "Error",
 err.response?.data?.message || "Cannot delete",
 "error"
 );
 }
 });
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Deduction Type Master" motherMenu="Masters" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Deduction Types</Card.Title>
 <Button onClick={openAdd}>+ Add Deduction Type</Button>
 </Card.Header>

 <Card.Body>
 {/* SEARCH */}
 <div className="mb-3">
 <Form.Control
 placeholder="Search deduction type..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </div>

 {/* LOADING */}
 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 ) : (
 <table className="table table-bordered table-hover">
 <thead className="table-light">
 <tr>
 <th>Name</th>
 <th>Description</th>
 <th>Status</th>
 <th width="170">Actions</th>
 </tr>
 </thead>
 <tbody>
 {filtered.map((item) => (
 <tr key={item._id}>
 <td>{item.name}</td>
 <td>{item.description || "-"}</td>
 <td>
 <Badge bg={item.status === "Active" ? "success" : "secondary"}>
 {item.status}
 </Badge>
 </td>
 <td>
 <Button
 size="sm"
 variant="warning"
 className="me-2"
 onClick={() => openEdit(item)}
 >
 Edit
 </Button>

 <Button
 size="sm"
 variant="danger"
 onClick={() => handleDelete(item)}
 >
 Delete
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 )}

 {!loading && filtered.length === 0 && (
 <div className="text-center text-muted py-4">
 No deduction types found
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* ================= MODAL ================= */}
 <Modal show={showModal} onHide={closeModal} backdrop={saving ? "static" : true}>
 <Modal.Header closeButton={!saving}>
 <Modal.Title>{editId ? "Edit Deduction Type" : "Add Deduction Type"}</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label>Name *</Form.Label>
 <Form.Control
 name="name"
 value={form.name}
 onChange={handleChange}
 placeholder="e.g. TDS"
 disabled={saving}
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Description</Form.Label>
 <Form.Control
 name="description"
 value={form.description}
 onChange={handleChange}
 as="textarea"
 rows={2}
 disabled={saving}
 />
 </Form.Group>

 <Form.Group>
 <Form.Label>Status</Form.Label>
 <Form.Select
 name="status"
 value={form.status}
 onChange={handleChange}
 disabled={saving}
 >
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Form.Group>
 </Modal.Body>

 <Modal.Footer>
 <Button variant="secondary" onClick={closeModal} disabled={saving}>
 Cancel
 </Button>
 <Button onClick={handleSave} disabled={saving}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default DeductionTypeMaster;
